<template>
  <div :class="cols">
    <div class="card max-height">
      <div class="card-header">
        <div class="caption" v-html="graphCaption"></div>
      </div>
      <div id="nodelink-container" class="card-body">
        <svg width="100%" height="100%" class="graph" shape-rendering="geometricPrecision" />
      </div>
    </div>
  </div>
</template>

<script>
import d3 from "@/assets/d3";
import lodash from "@/assets/lodash";
import constants from "@/constants";

export default {
  props: {
    cols: { type: String, required: true },
    bus: { type: Object, required: true }
  },
  data: function() {
    return {
      coloringMode: undefined,
      selection: {
        nodes: new Set(),
        timesteps: new Set(),
        variants: new Set()
      },
      graphCaption: undefined
    };
  },
  created() {
    this.dataService = this.$dataService;
    this.bus.$on(constants.VTS_SELECTED_EVENT, this.handleGridSelection);
    this.bus.$on(constants.NODE_SELECTED_EVENT, this.handleNodeSelection);
    this.bus.$on(constants.COLORING_EVENT, this.handleColoringSelection);
    this.bus.$on(constants.HOVER_EVENT, this.onHover);
  },
  mounted() {
    this.width = this.$el.getBoundingClientRect().width - 20; // respect container padding
    const height =
      document.getElementById("nodelink-container").getBoundingClientRect()
        .height - 10;
    const svg = d3.select("svg.graph");
    svg.attr("width", this.width).attr("height", height);

    const nodeLinkObject = this.dataService.toNodeLinkObject();
    const nodesData = nodeLinkObject.nodes;
    const linksData = nodeLinkObject.links;
    this.edgeScale = d3
      .scaleLinear()
      .domain([1, lodash.maxBy(linksData, "value").value])
      .range([1, this.$config.maxEdgeSize]);
    this.maxNodeValue = lodash.maxBy(nodesData, "value").value;

    // Singular names (e.g. node) contain the DOM elements
    this.linkEl = svg
      .append("g")
      .attr("class", "links")
      .selectAll("path");

    this.linkEl = this.linkEl
      .data(linksData, d => `${d.source}-${d.target}`)
      .enter()
      .append("path")
      .attr("class", "path selected")
      .attr("id", d => `link-${d.source}-${d.target}`)
      .merge(this.linkEl);

    this.nodeEl = svg
      .append("g")
      .attr("class", "nodes")
      .selectAll("g");

    this.nodeEl = this.nodeEl.data(nodesData, n => n.id);
    this.nodeEl = this.nodeEl
      .enter()
      .append("g")
      .merge(this.nodeEl);

    this.nodeEl
      .append("circle")
      .attr("node-id", d => d.id)
      // .attr("r", d => this.$config.nodeRadius(d.value, this.maxNodeValue))
      .attr("r", d => 20)
      .on("mouseover", this.hoverBarOver)
      .on("mouseout", this.hoverBarOut)
      .on("click", this.selectNodes);

    this.nodeEl.append("g").attr("node-id", d => d.id); // placeholder for pie charts

    this.nodeEl
      .append("text")
      .text(d => d.description)
      .attr("x", d => this.$config.nodeLabelPosX(d.value, this.maxNodeValue))
      .attr("y", 4);

    d3.forceSimulation(nodesData)
      .force(
        "link",
        d3
          .forceLink(linksData)
          .id(d => d.id)
          .distance(this.$config.nodeDistance(this.width))
      )
      .force(
        "charge",
        d3.forceManyBody().strength(this.$config.nodeCharge(this.width))
      )
      .force("center", d3.forceCenter(this.width / 2, height / 2))
      .on("tick", this.ticked);

    this.updateCaption();
  },
  methods: {
    ticked: function() {
      this.nodeEl.attr("transform", d => `translate(${d.x},${d.y})`);
      this.linkEl.attr("d", d =>
        d3.halfArrow(
          d,
          // this.$config.nodeRadius(d.target.value, this.maxNodeValue),
          20,
          this.edgeScale(d.value), // bodysize
          Math.max(3, this.edgeScale(d.value)) // headSize
        )
      );
      this.nodeEl
        .select("text")
        .attr(
          "x",
          d =>
            (this.width / 2 - d.x < 0 ? 1 : -1) *
            this.$config.nodeLabelPosX(d.value, this.maxNodeValue)
        )
        .attr("text-anchor", d => (this.width / 2 - d.x < 0 ? "start" : "end"));
    },
    nodesChanged() {
      d3.selectAll("circle").classed("selected", node =>
        this.selection.nodes.has(node.id)
      );

      this.colorizeNodes();

      // Trigger an update of the links by simply resending the "grid cell selected"-event.
      this.handleGridSelection({
        timesteps: this.selection.timesteps,
        variants: this.selection.variants
      });
    },
    hoverBarOver: function(node) {
      const msg = constants.HOVER_EVENT;
      const payload = {
        node: node.id,
        source: constants.COMPONENT_NAMES.NODELINK
      };
      this.bus.$emit(msg, payload);
    },
    hoverBarOut: function() {
      const msg = constants.HOVER_EVENT;
      const payload = {
        node: undefined,
        source: constants.COMPONENT_NAMES.NODELINK
      };
      this.bus.$emit(msg, payload);
    },
    onHover: function(e) {
      d3.selectAll("circle[node-id]").classed("hovered", false);
      d3.selectAll(`circle[node-id="${e.node}"]`).classed("hovered", true);
    },
    selectNodes: function(nodes) {
      if (nodes.id !== undefined) {
        if (!this.selection.nodes.delete(nodes.id)) {
          this.selection.nodes.add(nodes.id);
        }
      } else {
        this.selection.nodes = nodes;
      }

      this.nodesChanged();

      const msg = constants.NODE_SELECTED_EVENT;
      const payload = {
        nodes: this.selection.nodes,
        source: constants.COMPONENT_NAMES.NODELINK
      };
      this.bus.$emit(msg, payload);
    },
    handleGridSelection: function(e) {
      this.selection.timesteps = e.timesteps;
      this.selection.variants = e.variants;

      const selection = {
        nodes: this.selection.nodes,
        variants: this.selection.variants,
        timesteps: this.selection.timesteps
      };

      this.updatePieCharts();

      const filteredLinks = this.dataService.toNodeLinkObject(selection).links;

      d3.selectAll("path.path.selected").classed("selected", false);
      d3.selectAll(
        filteredLinks.map(l => `#link-${l.source}-${l.target}`).join(", ")
      ).classed("selected", true);
      this.updateCaption();
    },
    handleNodeSelection: function(e) {
      if (e.source !== constants.COMPONENT_NAMES.NODELINK) {
        this.selectNodes(e.nodes);
      }
      this.updateCaption();
    },
    colorizeNodes: function() {
      if (this.coloringMode === constants.COLORING_TYPE.NODES) {
        d3.select("svg.graph")
          .selectAll("circle[node-id]")
          .attr(
            "style",
            n =>
              `fill: ${
                this.selection.nodes.has(n.id)
                  ? this.$colorService.getColorById(n.id)
                  : "#FFFFFF"
              }`
          );
      }
    },
    removeColor: function() {
      d3.select("svg.graph")
        .selectAll("circle[node-id]")
        .attr("style", null);
    },
    handleColoringSelection: function(e) {
      this.coloringMode = this.coloringMode === e ? undefined : e;
      this.removeColor();
      this.updatePieCharts();
      this.colorizeNodes();
    },
    /**
     * Takes selection and current coloring mode into account.
     * Sums the aggregated degree (= in + out) per node, per selection.
     * Coloring mode decides whether to sum variants or timesteps.
     */
    updatePieCharts() {
      // In case we're coloring nodes, hide pie charts and exit.
      if (
        this.coloringMode === constants.COLORING_TYPE.NODES ||
        this.coloringMode === undefined
      ) {
        this.nodeEl.each(nodeEl => {
          d3.selectAll(`g[node-id="${nodeEl.id}"]`).attr("display", "none");
        });
        return;
      }

      let pieData = [];
      this.nodeEl.each(nodeEl => {
        pieData[nodeEl.id] =
          this.coloringMode === constants.COLORING_TYPE.TIMESTEPS
            ? [...Array(this.selection.timesteps.size)].fill(0)
            : [...Array(this.selection.variants.size)].fill(0);
      });

      // Use selected or all variants
      const variants =
        this.selection.variants.size > 0
          ? this.selection.variants
          : new Set(Object.keys(this.dataService.$data));

      // Use selected or all timesteps
      const timesteps =
        this.selection.timesteps.size > 0
          ? this.selection.timesteps
          : new Set(Object.keys(this.dataService.$data[0]));

      Array.from(variants).forEach((variant, idx_v) => {
        Array.from(timesteps).forEach((timestep, idx_ts) => {
          this.nodeEl.each(nodeEl => {
            const nodeData = this.dataService.$data[parseInt(variant)][
              parseInt(timestep)
            ][nodeEl.id];

            const selection_idx =
              this.coloringMode === constants.COLORING_TYPE.TIMESTEPS
                ? idx_ts
                : idx_v;

            pieData[nodeEl.id][selection_idx] += nodeData.agg;
          });
        });
      });

      this.nodeEl.each(nodeEl => {
        const arc = d3
          .arc()
          .outerRadius(() =>
            d3.select(`circle[node-id="${nodeEl.id}"]`).attr("r")
          )
          .innerRadius(0);

        const arcs = d3
          .pie()
          // .value(d => d.value)
          .sort(null)(pieData[nodeEl.id]);
        const pieGroup = d3.select(`g[node-id="${nodeEl.id}"]`);
        pieGroup.attr("display", "block");
        pieGroup
          .selectAll("path")
          .data(arcs)
          .join("path")
          .style("fill", d => this.$colorService.getColorById(d.index))
          .attr("d", arc);
      });
    },
    updateCaption() {
      let caption =
        "<em><a title='The graph is shown here as a node-link diagram with a force-directed layout'>Graph</a></em> ";
      const timesteps = this.selection.timesteps;
      const aggregatedHtml =
        "<a title='Graphs get aggregated by uniting the node and edge sets; edge counts are represented as the thickness of links'>aggregated</a>";
      switch (timesteps.size) {
        case 0:
        case this.dataService.dataIds.timesteps.length:
          caption += `${aggregatedHtml} across all timesteps `;
          break;
        case 1:
          caption += `at timestep t${timesteps.values().next().value} `;
          break;
        default:
          caption += `${aggregatedHtml} across ${timesteps.size} timesteps `;
      }
      const variants = this.selection.variants;
      switch (variants.size) {
        case 0:
        case this.dataService.dataIds.variants.length:
          caption += `and ${
            timesteps.size == 1 ? `${aggregatedHtml} across ` : ""
          }all variants `;
          break;
        case 1:
          caption += `in variant v${variants.values().next().value} `;
          break;
        default:
          caption += `and ${
            timesteps.size == 1 ? `${aggregatedHtml} across ` : ""
          }${variants.size} variants `;
      }
      const nodes = this.selection.nodes;
      switch (nodes.size) {
        case 0:
          break;
        case this.dataService.dataIds.nodes.length:
          caption += "with all nodes selected";
          break;
        case 1:
          caption += "with one node selected";
          break;
        default:
          caption += `with ${nodes.size} nodes selected`;
      }
      this.graphCaption = caption;
    }
  }
};
</script>

<style lang="scss">
svg.graph {
  path.path {
    opacity: 0;
    stroke-width: 1;

    stroke: #999;
    fill: #999;
    &.selected {
      opacity: 1;
    }
  }

  circle {
    stroke: #7f8c8d;
    fill: #fff;
    stroke-width: 2;

    &:hover {
      stroke: #111;
      fill: #ecf0f1;
      cursor: pointer;
      stroke-width: 3;
    }

    &.selected,
    &.hovered {
      stroke: #111;
      fill: #ecf0f1;
      stroke-width: 3;
    }
  }

  circle.selected ~ text {
    font-weight: bold;
  }
}
</style>
